import React, { useCallback, useMemo } from "react";

import Col from "antd/es/col";
import Form from "antd/es/form";
import Row from "antd/es/row";
import Select from "antd/es/select";
import Tag from "antd/es/tag";
import { useIntl } from "react-intl";

import ActivityVisibility from "@mapmycustomers/shared/enum/activity/ActivityVisibility";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import Visibility from "@mapmycustomers/shared/enum/Visibility";
import CustomFieldType from "@mapmycustomers/shared/types/customField/CustomField";
import {
  EntityTypesSupportingFieldCustomization,
  EntityTypeSupportingGroups,
  Group,
} from "@mapmycustomers/shared/types/entity";
import type ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import type Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import type Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import FunnelStageValue from "@mapmycustomers/shared/types/FunnelStageValue";
import { ChildLayout, LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import type Team from "@mapmycustomers/shared/types/Team";
import type User from "@mapmycustomers/shared/types/User";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import {
  Alert,
  DatePicker,
  NumberField,
  SelectField,
  TextAreaWithMentions,
  TextField,
} from "@mapmycustomers/ui";

import CalculatedFieldInfoIcon from "@app/component/CalculatedFieldInfoIcon";
import CircleColorPicker from "@app/component/CircleColorPicker";
import AddressFormItem from "@app/component/createEditEntity/AddressFormItem";
import CustomFieldComponent from "@app/component/CustomField";
import EntityBadge from "@app/component/EntityBadge";
import FrequencyIntervalFormEditor from "@app/component/frequency/FrequencyIntervalFormEditor";
import GroupsSelection from "@app/component/GroupsSelection";
import CurrencyInput from "@app/component/input/CurrencyInput";
import PhoneTextField from "@app/component/input/PhoneTextField";
import UserSelectField from "@app/component/input/UserSelectField";
import getCustomFieldsValidationRules from "@app/component/input/utils/getCustomFieldsValidationRules";
import VisibilitySelectField from "@app/component/input/VisibilitySelectField";
import WebsiteTextField from "@app/component/input/WebsiteTextField";
import EmailTextFieldWithActions from "@app/component/preview/components/EmailTextFieldWithActions";
import DealLossCommentField from "@app/scene/settings/component/FormLayouts/component/Canvas/components/DealLossCommentField";
import DealLossReasonField from "@app/scene/settings/component/FormLayouts/component/Canvas/components/DealLossReasonField";
import layout from "@app/styles/layout";
import { MAX_DEAL_SCORE } from "@app/util/consts";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import { isCalculatedField, isCustomField } from "@app/util/fieldModel/impl/assert";
import { formatDate } from "@app/util/formatters";
import useFirstLastNameRequirement from "@app/util/hook/entityForm/useFirstLastNameRequirement";
import useActivityVisibilityOptions from "@app/util/hook/useActivityVisibilityOptions";
import defaultSelectOptionMapper from "@app/util/mappers/defaultSelectOptionMapper";
import { getActivityVisibilityNote } from "@app/util/ui";
import useActivityTypeOptions from "@app/util/ui/useActivityTypeOptions";

import File from "../components/File";

import styles from "./useFormFieldRenderer.module.scss";

const useFormFieldRenderer = (
  activeSchema: LayoutSchemaField[],
  childLayout: ChildLayout | undefined,
  activityTypes: ActivityType[],
  activityTypeDefaultValue: ActivityType["id"] | undefined,
  entityType: EntityTypesSupportingFieldCustomization,
  funnels: Funnel[],
  funnelStages: Record<Funnel["id"], Stage[]>,
  defaultFunnelStage: Partial<FunnelStageValue>,
  groups: Group[],
  currentUser: User,
  users: User[],
  allTeams: Team[]
) => {
  const intl = useIntl();

  const visibilityOptions = useActivityVisibilityOptions(true);

  const activityTypeOptions = useActivityTypeOptions(
    activityTypes,
    childLayout?.crmActivityType?.id ?? activityTypeDefaultValue,
    useCallback((type) => !type.active, [])
  );

  const [firstNameRequired, lastNameRequired] = useFirstLastNameRequirement(activeSchema);

  const funnelOptions = useMemo(() => funnels.map(defaultSelectOptionMapper), [funnels]);
  const stagesOptions = useMemo(
    () =>
      childLayout?.funnel?.id
        ? funnelStages[childLayout.funnel.id]
        : defaultFunnelStage.funnel?.id
        ? funnelStages[defaultFunnelStage?.funnel?.id]
        : [],
    [funnelStages, defaultFunnelStage.funnel?.id, childLayout?.funnel?.id]
  );

  const getCustomFieldLabel = useCallback((field: CustomFieldType) => {
    if (field.isCalculated) {
      return (
        <div className={styles.calculatedFieldLabel}>
          <div>{field.displayName}</div>
          <div>
            <CalculatedFieldInfoIcon customField={field} />
          </div>
        </div>
      );
    }
    return field.displayName;
  }, []);

  return useDynamicCallback((field: IField | undefined, row: LayoutSchemaField) => {
    // we assume field is required via isSystemRequired only if it's not a relationship field
    const required = row.required || field?.isSystemRequired;
    const requiredRules = required ? [{ required: true }] : undefined;

    if (row.field === "parentAccountId") {
      return (
        <>
          <Form.Item
            label={intl.formatMessage({
              id: "createCompanyModal.advancedFields.selectParent",
              defaultMessage: "Select Parent Company",
              description: "Parent Company field on the Create Company modal",
            })}
            name="parentCompanyName"
            required={required}
          >
            <TextField readOnly />
          </Form.Item>
        </>
      );
    }

    if (row.field === "files") {
      return <File entityType={entityType} required={required} />;
    }

    if (row.field === "address") {
      return <AddressFormItem required={required} />;
    }

    if (row.field === "assigneeId") {
      return (
        <Form.Item label={field?.displayName} name={"assignee"} required={required}>
          <UserSelectField label={false} tabIndex={-1} users={users} value={currentUser} />
        </Form.Item>
      );
    }

    if (row.field === "firstName") {
      return (
        <Row align="middle" className={styles.fullWidth} gutter={layout.spacerS}>
          <Col span={12}>
            <Form.Item
              label={intl.formatMessage({
                id: "createPersonModal.basicFields.firstName",
                defaultMessage: "First Name",
                description: "First name field on the Create Person modal",
              })}
              required={firstNameRequired || (!firstNameRequired && !lastNameRequired)}
            >
              <TextField />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={intl.formatMessage({
                id: "createPersonModal.basicFields.lastName",
                defaultMessage: "Last Name",
                description: "Last name field on the Create Person modal",
              })}
              required={lastNameRequired || (!firstNameRequired && !lastNameRequired)}
            >
              <TextField />
            </Form.Item>
          </Col>
        </Row>
      );
    }

    if (!field) {
      return <TextField tabIndex={-1} />;
    }

    if (field.hasFeature(FieldFeature.ACTIVITY_TYPE_FIELD)) {
      return (
        <Form.Item
          label={field?.displayName}
          name={ActivityFieldName.ACTIVITY_TYPE}
          required={required}
        >
          <SelectField<ActivityType["id"]>
            options={activityTypeOptions}
            placeholder={"Select Activity Type"}
            tabIndex={-1}
          />
        </Form.Item>
      );
    }

    if (field.isCompound) {
      return null;
    }

    if (field.hasFeature(FieldFeature.FUNNEL_FIELD)) {
      return (
        <div className={styles.twoFields}>
          <div>
            <Form.Item label={field.displayName} name={DealFieldName.FUNNEL} required={required}>
              <SelectField<Funnel["id"]>
                options={funnelOptions}
                placeholder={"Select Funnel"}
                tabIndex={-1}
              />
            </Form.Item>
          </div>

          <div>
            <Form.Item
              label={"Stage"}
              name={DealFieldName.STAGE}
              required={required}
              rules={requiredRules}
            >
              <SelectField<Stage["id"]>
                defaultValue={childLayout?.stage?.id}
                placeholder={"Select Stage"}
                tabIndex={-1}
              >
                {stagesOptions.map((stage) => (
                  <Select.Option key={stage.id} value={stage.id}>
                    <EntityBadge entity={stage} />
                  </Select.Option>
                ))}
              </SelectField>
            </Form.Item>
          </div>
        </div>
      );
    }

    if (field.hasFeature(FieldFeature.ADDRESS)) {
      return <TextField readOnly tabIndex={-1} />;
    }

    if (isCustomField(field)) {
      const definition = field.customFieldData;
      return (
        <Form.Item
          label={getCustomFieldLabel(definition)}
          name={["customFields", String(definition.id)]}
          required={required}
          rules={required ? getCustomFieldsValidationRules(intl, definition) : undefined}
        >
          {!isCalculatedField(field) ? (
            <CustomFieldComponent definition={definition} entityType={entityType} />
          ) : (
            <div>
              <span className={styles.calculatedFieldPlaceholder}>
                {intl.formatMessage({
                  id: "settings.formLayouts.canvas.calculatedField.placeholder",
                  defaultMessage: "Value will show here",
                  description:
                    "A text to show instead of the undefined calculated custom field value",
                })}
              </span>
              <Alert
                className={styles.disclaimer}
                message={intl.formatMessage({
                  id: "settings.formLayouts.canvas.calculatedField.alert",
                  defaultMessage:
                    "Calculated fields cannot be edited. They will be visible in both edit mode layout and create layout.",
                  description: "Alert for the Calculated field in Layout constructor",
                })}
                showIcon
                type="info"
              />
            </div>
          )}
        </Form.Item>
      );
    }

    const isDateField = ["closingDate"].includes(field.name);
    if (isDateField) {
      return (
        <Form.Item label={field.displayName} name={["default", row.field]} required={required}>
          <DatePicker allowClear={false} format={"PP"} style={{ width: "100%" }} />
        </Form.Item>
      );
    }

    if (field.name === DealFieldName.DEAL_LOSS_REASON) {
      return (
        <DealLossReasonField field={field} required={required} row={row} showInfo={!childLayout} />
      );
    }

    if (field.name === DealFieldName.DEAL_LOSS_COMMENT) {
      return <DealLossCommentField field={field} required={required} showInfo={!childLayout} />;
    }

    const isReadOnlyDateField = ["createdAt", "updatedAt"].includes(field.name);
    if (isReadOnlyDateField) {
      return <Tag>{formatDate(Date.now(), "PPPpp")}</Tag>;
    }

    if (field.name === "name") {
      return (
        <Form.Item
          label={field.displayName}
          name={["default", row.field]}
          required={required}
          rules={requiredRules}
        >
          <TextField />
        </Form.Item>
      );
    }

    if (field.name === "website") {
      return (
        <Form.Item
          label={field.displayName}
          name={["default", row.field]}
          required={required}
          rules={requiredRules}
        >
          <WebsiteTextField readOnly />
        </Form.Item>
      );
    }

    if (field.name === "email") {
      return (
        <Form.Item
          label={field.displayName}
          name={["default", row.field]}
          required={required}
          rules={requiredRules}
        >
          <EmailTextFieldWithActions readOnly />
        </Form.Item>
      );
    }

    if (field.hasFeature(FieldFeature.COLOR_FIELD)) {
      return (
        <Form.Item label={field.displayName} name={["default", row.field]} required={required}>
          <CircleColorPicker />
        </Form.Item>
      );
    }

    if (field.name === "numEmployees") {
      return (
        <Form.Item label={field.displayName} name={["default", row.field]} required={required}>
          <NumberField min={1} readOnly />
        </Form.Item>
      );
    }

    if (row.field === ActivityFieldName.NOTE) {
      return (
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, curValues) =>
            prevValues.default?.visibility !== curValues.default?.visibility
          }
        >
          {({ getFieldValue }) => (
            <Form.Item label={field.displayName} name={["default", "note"]} required={required}>
              <TextAreaWithMentions
                allUsers={users}
                key={getFieldValue(["default", "visibility"])}
                maxLength={Number.MAX_SAFE_INTEGER}
                teams={allTeams}
              />
            </Form.Item>
          )}
        </Form.Item>
      );
    }

    if (row.field === ActivityFieldName.VISIBILITY) {
      return (
        <Form.Item
          label={intl.formatMessage({
            id: "createEditActivityModal.field.visibility",
            defaultMessage: "Visible to",
            description: "Visibility field label in Create create edit activity modal",
          })}
          required={required}
        >
          <VisibilitySelectField<ActivityVisibility>
            noteGetter={getActivityVisibilityNote}
            options={visibilityOptions}
            teams={allTeams.filter(({ viaTeamAccess }) => viaTeamAccess)}
            value={Visibility.PRIVATE}
          />
        </Form.Item>
      );
    }

    if (field.name === "score") {
      return (
        <Form.Item label={field.displayName} name={["default", row.field]} required={required}>
          <NumberField
            inputClassName={styles.fullWidth}
            max={MAX_DEAL_SCORE}
            min={0}
            placeholder={intl.formatMessage(
              {
                id: "createDealModal.field.score.placeholder",
                defaultMessage: "0-{max}",
                description: "Score field's placeholder on the Create Deal modal",
              },
              { max: MAX_DEAL_SCORE }
            )}
            precision={0}
          />
        </Form.Item>
      );
    }

    if (field.name === "annualRevenue") {
      return (
        <Form.Item label={field.displayName} name={["default", row.field]} required={required}>
          <CurrencyInput
            allowEmpty
            // Platform limit: https://mapmycustomers.atlassian.net/browse/TART-18901?focusedCommentId=51570
            max={Number.MAX_SAFE_INTEGER}
            min={0}
          />
        </Form.Item>
      );
    }

    if (field.name === DealFieldName.AMOUNT) {
      return (
        <Form.Item label={field.displayName} name={["default", row.field]} required={required}>
          {/* eslint-disable-next-line no-loss-of-precision */}
          <CurrencyInput allowEmpty max={99999999999999.99} min={0} />
        </Form.Item>
      );
    }

    if (field.name === "phone") {
      return (
        <Form.Item label={field.displayName} name={["default", row.field]} required={required}>
          <PhoneTextField readOnly />
        </Form.Item>
      );
    }

    if (field.hasFeature(FieldFeature.GROUP_FIELD)) {
      return (
        <Form.Item
          label={field.displayName}
          name={row.field}
          required={required}
          valuePropName="selectedGroupIds"
        >
          <GroupsSelection entityType={entityType as EntityTypeSupportingGroups} groups={groups} />
        </Form.Item>
      );
    }

    if (field.hasFeature(FieldFeature.FREQUENCY_INTERVAL)) {
      return (
        <Form.Item label={field.displayName} name={["default", row.field]} required={required}>
          <FrequencyIntervalFormEditor />
        </Form.Item>
      );
    }

    return (
      <Form.Item label={field.displayName} name={["default", row.field]} required={required}>
        <TextField readOnly tabIndex={-1} />
      </Form.Item>
    );
  });
};

export default useFormFieldRenderer;
